import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {Alert, Snackbar} from '@mui/material';
import {registerNotifier} from '../utils/notifications';

const NotificationContext = createContext({
    notify: () => {}
});

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({children}) => {
    const [queue, setQueue] = useState([]);
    const [current, setCurrent] = useState(null);
    const [open, setOpen] = useState(false);

    const popNext = useCallback(() => {
        setQueue((prev) => {
            if (!prev.length) {
                setCurrent(null);
                return prev;
            }
            const [next, ...rest] = prev;
            setCurrent(next);
            setOpen(true);
            return rest;
        });
    }, []);

    const notify = useCallback((payload) => {
        if (!payload?.message) return;
        setQueue((prev) => [...prev, {
            type: payload.type || 'info',
            message: payload.message
        }]);
    }, []);

    React.useEffect(() => {
        registerNotifier(notify);
    }, [notify]);

    React.useEffect(() => {
        if (!open && !current && queue.length) {
            popNext();
        }
    }, [open, current, queue.length, popNext]);

    const handleClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    const handleExited = () => {
        setCurrent(null);
    };

    const value = useMemo(() => ({notify}), [notify]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={4200}
                onClose={handleClose}
                TransitionProps={{onExited: handleExited}}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert onClose={handleClose} severity={current?.type || 'info'} variant="filled" sx={{width: '100%'}}>
                    {current?.message || ''}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

